import moment from 'moment';
import Logging from '../service/logging';

import { IApplicationState } from '.';
import { BookingStatus } from './Booking';
import { Routes, readAccessCodeFromLocation } from '../helpers/routes';
import { languageList, ZLLanguage } from '../helpers/lang';

import { GiftVoucherStatus } from './GiftVoucher';
import { PurchaseTypes } from './PurchaseTypes';
import { History } from 'history';
 import { appInsights } from "../Logging/appInsights";
import { FlagList, FlagSource, FlagVenueList, initialFlagValues, createUserSessionId } from './featureFlags';
import { CustomerDataState } from './Customer';
import { readAccessCode } from './sessionAccessCode';

// i18next reads 'i18nextLng' from localStorage on its own startup, before our Redux store exists.
// We read it here at module level so that initState.intl.selectedLanguage matches what i18next
// will bootstrap with, keeping the two in sync before loadState() runs.
const storedCode = localStorage.getItem('i18nextLng');

const detectedLanguage = window.navigator.language;
const storedLanguage = languageList.find((language: ZLLanguage) =>
    language.code.startsWith(storedCode ?? detectedLanguage)
) ?? languageList[0];

export const initState: IApplicationState = {
    sites: {
        preselectedSite: undefined,
        selectedSite: undefined,
        selectedSiteUI: false,
        sitesLoading: true,
        sites: [],
        sitesLoaded: false,
        nearestSite: undefined,
        globalSite: undefined,
        globalSiteLoaded: false,
        globalSiteLoading: false,
        //the preview access code is not persisted here - loadState() overlays it from
        //sessionStorage so it survives every clear/reset path below
        accessCode: undefined
    },
    games: {
        gamesLoading: false,
        games: [],
        selectedGame: undefined,
        gamesLoaded: false,
    },
    players: {
        selectedPlayers: undefined,
        selectedPlayersUI: false,
        selectedPrivateUpgrade: false,
    },
    premiumPtv: {
        purchaseDetails: undefined,
        loading: false,
    },
    booking: {
        booking: undefined,
        loading: false,
        inFlight: {},
        failed: false,
        charge: undefined,
        status: BookingStatus.Unknown,
        failedMessageKey: undefined,
        appliedCodes: [],
        readyForPayment: false,
        availableAddOns: undefined,
        availableDigitalAddOns: undefined
    },
    customer: {
        isValid: false,
        emailSubscribed: false,
        smsSubscribed: false,
    },
    location: {
        location: undefined,
        loading: false,
        locationFailed: false,
        locationKnown: false,
        reverseGeoCode: undefined,
    },
    session: {
        openDatesLoaded: false,
        openDates: undefined,
        openDatesLoading: false,
        openDatesLoadedThrough: undefined,
        openDatesExtending: false,
        openDatesExhausted: false,
        sessions: undefined,
        sessionTimes: undefined,
        selectedDate: undefined,
        sessionTimesLoaded: false,
        sessionTimesLoading: false,
        selectedSession: undefined,
        selectedDateUI: false,
        selectedTimeUI: false,
        selectedSessionUI: false,
    },
    intl: {
        selectedLanguage: storedLanguage,
    },
    invoice: {
        loading: false,
        appliedCodes: []
    },
    ui: {
        errorKey: undefined,
        errorMessage: undefined,
        previewMode: false
    },
    payments: {
        stripe: {
            apiKey: undefined,
            paymentMethodType: undefined
        },
        paypal: {
            apiKey: undefined,
        },
        checkout: {
            apiKey: undefined,
        },
        custom: undefined,
        loading: false,
        gatewaysLoading: false,
        backProcessing: false,
        clientSecret: undefined,
    },
    attribution: {
        utm_Id: undefined,
        utm_campaign: undefined,
        utm_content: undefined,
        utm_medium: undefined,
        utm_source: undefined,
        utm_term: undefined,
        internal_source: undefined,
        internal_sourcePage: undefined,
        internal_componentType: undefined,
        internal_componentInternalName: undefined,
        internal_componentTitle: undefined,
        internal_buttonText: undefined,
    },
    appSettings: {
        environment: 'dev',
        gtmEndpoint: '',
        gtmSendToClients: false,
        logRocketId: '',
        brazeApiKey: '',
        brazeEndpoint: '',
        newsletterEmailGroup: '',
        updatesEmailGroup: '',
        offersEmailGroup: '',
        auSmsGroup: '',
        usSmsGroup: '',
        caSmsGroup: '',
        settingsLoaded: false,
        appInsightsKey: '',
        configCatSdkKey: '',
        globalWebsiteUrl: ''
    },
    purchaseType: {
        selectedPurchaseType: PurchaseTypes.Booking,
        selectedPurchaseTypeUI: false,
    },
    giftVoucher: {
        loading: false,
        configLoading: false,
        failed: false,
        configDetails: undefined,
        charge: undefined,
        giftVoucher: undefined,
        status: GiftVoucherStatus.Unknown,
        selectedFixedAmountButtonIndex: undefined,
        amount: undefined,
        toEmail: undefined,
        toFullName: undefined,
        message: undefined,
        selectedGiftVoucherAmountUI: false,
        selectedGiftVoucherDetailsUI: false,
        toRecipientSelected: undefined,
        recipientDetailsValid: false,
    },
    microCopy: {
        microCopyMap: {},
        microCopyBundles: {},
    },
    featureFlags: {
        flags: { ...initialFlagValues },
        lastLoadedVenueId: undefined,
        flagVenueSettings: {},
        loading: false,
        hasLoaded: false,
        abandoned: false,
        userSessionId: undefined
    },
    currencies: {
        currencies:[],
        isLoading: false,
        hasLoaded: false,
        loadFailed: false
    },
    paymentOption: {
        selectedOption: 'full',
        splitSlotCount: 1,
        splitCreated: false,
        splitLoading: false,
        splitShares: [],
    },
    splitPaymentLanding: {
        share: undefined,
        loading: false,
        error: false,
        errorType: undefined,
        voucherLoading: false,
        voucherError: false,
        appliedVoucherCode: undefined,
        allocationLoading: false,
        allocationError: false,
        declined: false,
    },
    splitAllocate: {
        info: undefined,
        loading: false,
        error: false,
        errorType: undefined,
        shareToken: undefined,
        submitCreated: false,
        submitLoading: false,
        submitShares: [],
        mutatingShareToken: undefined,
        mutateError: false,
    },
    splitJoin: {
        loading: false,
        error: false,
        errorType: undefined,
        joinInfo: undefined,
        submitError: false,
        submitting: false,
    },
    balancePaymentLanding: {
        booking: undefined,
        loading: false,
        polling: false,
        error: false,
        errorType: undefined,
    },
    rewards: {
        flows: [],
        flowsLoading: false,
        flowsLoaded: false,
        flowsSiteId: undefined,
        upsellWasShown: false,
        initialPlayerCount: undefined,
    },
};

const getOrCreateUserSessionId = (currentSessionId: string|undefined): string => {

    //if we are about to put a valid session id back into the active state then we don't need to generate a new one
    if (currentSessionId && currentSessionId.trim().length > 0) {
        return currentSessionId;
    }

    //otherwise this is a totally fresh session and we need to generate a new id for it
    const newId = createUserSessionId();
    return newId;
}


const loadStateInternal = (history: History) => {
    try {
        console.log('Running load state from local storage');
        const serialisedState = localStorage.getItem('state');
        const serialisedLastSeen = localStorage.getItem('lastSeen');

        //make sure the init state has a random generated id as a fallback if we don't have one to restore from a previous session
        initState.featureFlags.userSessionId = getOrCreateUserSessionId(undefined);

        if (serialisedState === null || serialisedLastSeen === null) {
            //prep and use the empty initial state
            return initState;
        }

        //if we loading the processing page don't clear the state
        //would be nice to be able to figure out if the previous page was something different
        let userMakingPaymentTransition = false;
        let returnClearState = false;
        let returnAttributionData = false;
        let returnFeatureFlagData = false;
        let returnCustomerData = false;

        if (history.location.pathname.includes('/' + Routes.Processing) || history.location.pathname.includes('/' + Routes.SplitPayment + '/')) {
            userMakingPaymentTransition = true;
        }

        //figure out if we should keep the local state or if it is due to be cleared/reset
        const state: IApplicationState = JSON.parse(serialisedState);
        const staleMinutes = Number(process.env.REACT_APP_STALE_STATE_TIMER) ?? 20;
        const minutes = moment().utc().diff(moment(serialisedLastSeen), 'minutes');

        //update the state we just read in from memory to reflect the feature flag source
        if (state.featureFlags) {
            //update all the regular flags to mark them as restored
            const restoredFlags: FlagList = {};
            if (state?.featureFlags?.flags) {
                for (const key in state?.featureFlags?.flags) {
                    const flag = state?.featureFlags?.flags[key];
                    if (flag) {
                        restoredFlags[key] = { ...flag, source: FlagSource.Restored };
                    }
                }
            }

            //update all the venue settings to mark them as restored
            const restoredVenueSettings: FlagVenueList = {};
            if (state?.featureFlags?.flagVenueSettings) {
                for (const flagKey in state?.featureFlags?.flagVenueSettings) {

                    const venueMap = state?.featureFlags?.flagVenueSettings[flagKey];
                    if (!venueMap) continue;

                    restoredVenueSettings[flagKey] = {};

                    for (const venueIdStr in venueMap) {
                        const venueId = parseInt(venueIdStr, 10);
                        const flag = venueMap[venueId];
                        if (flag) {
                            restoredVenueSettings[flagKey][venueId] = { ...flag, source: FlagSource.Restored };
                        }
                    }
                }
            }

            //use the session id from storage or ensure we generate a new one
            const userSessionId = getOrCreateUserSessionId(state?.featureFlags?.userSessionId);

            state.featureFlags = {
                ...state.featureFlags,
                flags: restoredFlags,
                flagVenueSettings: restoredVenueSettings,
                loading: false,
                hasLoaded: false,
                abandoned: false,
                userSessionId: userSessionId
            };
        }
        else {
            //if we don't have a previous feature flag state, update it to have one
            state.featureFlags = initState.featureFlags;
        }

        // Mark customer data as restored if it has meaningful identity content
        if (state.customer && (state.customer.emailAddress || state.customer.fullName || state.customer.phoneNumber)) {
            state.customer = {
                ...state.customer,
                dataState: CustomerDataState.Restored,
            };
        }

        // After X minutes we throw away the booking state and return the default values
        // After X days we also throw out the marketing attribution data
        // After X days we also clear / forget any previous feature flag config
        if (minutes > staleMinutes) {
            const attributionStaleDays = Number(process.env.REACT_APP_STALE_ATTRIBUTION_DAYS) ?? 30;
            const featureFlagStaleDays = Number(process.env.REACT_APP_STALE_FEATURE_DAYS) ?? 30;
            const days = moment().utc().diff(moment(serialisedLastSeen), 'days');

            console.log('state is stale');
            returnClearState = true;

            // if the marketing isn't too old, make sure it's preserved
            if (days < attributionStaleDays) {
                returnAttributionData = true;
            } else {
                console.log(`state attribution data is stale, days:${days}, attributionStaleDays:${attributionStaleDays}`);
            }

            // if the feature flag data isn't too old keep that too (to preserve stick feature testing)
            if (days < featureFlagStaleDays) {
                returnFeatureFlagData = true;
            } else {
                console.log(`state feature flag data is stale, days:${days}, featureFlagStaleDays:${featureFlagStaleDays}`);
            }

            // if the customer data isn't too old keep that too
            if (days < attributionStaleDays) {
                returnCustomerData = true;
            } else {
                console.log(`customer data is stale, days:${days}, staleDays:${attributionStaleDays}`);
            }

        }
        //if we have booking or gift voucher that is not 'in progress' (eg already completed or failed)
        //return the blank slate with marketing attribution data
        else if (
            state.purchaseType.selectedPurchaseType === PurchaseTypes.Booking ||
            state.purchaseType.selectedPurchaseType === PurchaseTypes.PrivateEvent
        ) {
            if (
                !state.booking.booking ||
                state.booking.status === BookingStatus.Active ||
                state.booking.status === BookingStatus.Failed ||
                state.booking.status === BookingStatus.Reserved
            ) {
                console.log(`booking state is ${state.booking.status} and should be cleared`);
                returnClearState = true;
                returnAttributionData = true;
                returnFeatureFlagData = true;
                returnCustomerData = true;
            }
        } else if (
            state.purchaseType.selectedPurchaseType === PurchaseTypes.GiftVoucher ||
            state.purchaseType.selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher
        ) {
            if (
                !state.giftVoucher.giftVoucher ||
                state.giftVoucher.status === GiftVoucherStatus.Completed ||
                state.giftVoucher.status === GiftVoucherStatus.Cancelled ||
                state.giftVoucher.status === GiftVoucherStatus.Failed ||
                state.giftVoucher.status === GiftVoucherStatus.Reserved
            ) {
                console.log(`gift voucher state is ${state.giftVoucher.status} and should be cleared`);
                returnClearState = true;
                returnAttributionData = true;
                returnFeatureFlagData = true;
                returnCustomerData = true;
            }
        }

        //return the blank state,
        if (returnClearState) {
            if (userMakingPaymentTransition) {
                //unless we are making some delayed payment transition(eg stripe 3Ds)
                //where the user has just successfully paid from something we had thought was expired or abandoned
                console.log('User making payment after local state should have expired, preserving state.');
                let newError = new Error('User making payment after local state should have been cleared');
                newError.name = 'Late Payment State Preservation';
                Logging.LogError(newError);
            } else {
                console.log(`Returning clear state ${returnAttributionData ? ' + attribution' : ''} ${returnFeatureFlagData ? ' + feature flags' : ''}`);
                let returnState = { ...initState };

                //grab thru any price set data
                returnState.booking = {
                    ...initState.booking,
                    priceSets: state.booking.priceSets,
                };

                //grab attribution if still valid
                if (returnAttributionData) {
                    returnState.attribution = state.attribution;
                }

                //grab feature flags if still valid
                if (returnFeatureFlagData) {
                    returnState.featureFlags = state.featureFlags;
                }

                // preserve customer identity if not past long-term staleness
                if (returnCustomerData && state.customer &&
                    (state.customer.emailAddress || state.customer.fullName || state.customer.phoneNumber)) {
                    returnState.customer = {
                        ...state.customer,
                        dataState: CustomerDataState.Restored,
                        isValid: false,
                    };
                }

                return returnState;
            }
        }

        //otherwise return the state that we already have as it should still be relevant
        //either because it's still fresh or because the user is finalizing a stripe 3Ds payment
        //and we need the details to show them the result of their transaction
        state.ui.errorKey = undefined;
        state.ui.errorMessage = undefined;

        // Replace any old intl state shape or missing selectedLanguage with a clean default
        if (!(state.intl as any).selectedLanguage) {
            state.intl = { ...initState.intl };
        }

        // Defensive reset: any in-flight booking requests die with the page that issued them.
        // Without this, a tab crash mid-request would leave the rehydrated app permanently "loading".
        if (state.booking) {
            state.booking.inFlight = {};
            state.booking.loading = false;
        }

        console.log('Returning previous state');
        return state;
    } catch (err) {
        console.log('state error, clearing state: ' + err);

         appInsights?.trackException(
             { error: new Error(`Error loading local state`), severityLevel: 2},
             { exception: err, sourceFile: "localStorage.ts", product: "booking" });

        if (err instanceof Error) {
            let newError = new Error(err.message);
            newError.name = 'State loading error';
            Logging.LogError(newError);
        } else {
            let newError = new Error('Unknown exception occurred while trying to load state');
            newError.message = 'State loading error';
            Logging.LogError(newError);
        }

        //make sure we have a session id
        initState.featureFlags.userSessionId = getOrCreateUserSessionId(undefined);

        return initState;
    }
};

// loadStateInternal has several return paths (empty init state, cleared state, restored
// state, and the error fallback) and most of them throw away the sites slice. Rather than
// teaching each one about the access code, overlay it once here.
//
// This runs in index.tsx before the store is created - and therefore before any component
// mounts - which is the only safe place to seed the code. It cannot be left to a mount
// effect: React flushes child effects before parent ones, so the tickets page's
// loadSites() would fire (and request /sites with no code, hiding the preview venue)
// before an App-level effect could dispatch it.
//
// The URL wins over sessionStorage, so a fresh deep link overrides a stale code in the
// same tab. We always assign, so an access code left behind in the localStorage 'state'
// blob by an earlier app version is scrubbed rather than restored.
export const loadState = (history: History): IApplicationState => {
    const state = loadStateInternal(history);
    const accessCode = readAccessCodeFromLocation(history.location) ?? readAccessCode();
    return { ...state, sites: { ...state.sites, accessCode } };
};

export const saveState = (state: IApplicationState) => {
    try {
        const serialisedState = JSON.stringify(state);
        const date = moment().utc().toISOString();
        localStorage.setItem('state', serialisedState);
        localStorage.setItem('lastSeen', date);
        if (state.intl.selectedLanguage.code) {
            localStorage.setItem('i18nextLng', state.intl.selectedLanguage.code);
        }
    } catch (err) {
        console.log(err);
         appInsights?.trackException(
             { error: new Error(`Error saving local state`), severityLevel: 2},
             { exception: err, sourceFile: "localStorage.ts", product: "booking" });
    }
};
